import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getCurrentToken } from '@/lib/api';
import type {
  Client,
  ClientContact,
  Document,
  Deadline,
  TaxObligation,
  PaginatedResponse,
  CreateClientRequest,
  UpdateClientRequest,
  ClientListParams,
  DocumentListParams,
  DeadlineListParams,
  UpdateDeadlineRequest,
  CreateContactRequest,
} from '@taxistry/types';

function toQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  return q.toString();
}

// ─── Clients ──────────────────────────────────

export function useClients(params: ClientListParams = {}) {
  return useQuery({
    queryKey: ['clients', params],
    queryFn: () => api.get<PaginatedResponse<Client>>(`/clients?${toQuery(params as Record<string, unknown>)}`),
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get<Client & { contactsCount: number; documentsCount: number; nextDeadline: Deadline | null }>(`/clients/${id}`),
    enabled: !!id,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateClientRequest) => api.post<Client>('/clients', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientRequest }) =>
      api.patch<Client>(`/clients/${id}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', vars.id] });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/clients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

// ─── Contacts ─────────────────────────────────

export function useContacts(clientId: string | undefined) {
  return useQuery({
    queryKey: ['contacts', clientId],
    queryFn: () => api.get<{ data: ClientContact[] }>(`/contacts/client/${clientId}`),
    enabled: !!clientId,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, data }: { clientId: string; data: CreateContactRequest }) =>
      api.post<ClientContact>(`/contacts/client/${clientId}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['contacts', vars.clientId] });
    },
  });
}

export function useDeactivateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch(`/contacts/${id}/deactivate`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

// ─── Documents ────────────────────────────────

export function useDocuments(params: DocumentListParams = {}) {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: () => api.get<PaginatedResponse<Document & { clientBusinessName?: string }>>(`/documents?${toQuery(params as Record<string, unknown>)}`),
  });
}

export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: ['document', id],
    queryFn: () => api.get<Document & { signedUrl: string; thumbnailUrl: string | null }>(`/documents/${id}`),
    enabled: !!id,
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, clientId }: { file: File; clientId: string }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('clientId', clientId);

      const token = await getCurrentToken();

      const res = await fetch('/api/documents/upload', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useUpdateDocumentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/documents/${id}/status`, { status }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['document', vars.id] });
    },
  });
}

export function useConfirmExtraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, extraction }: { id: string; extraction: Record<string, unknown> }) =>
      api.post(`/documents/${id}/confirm`, { confirmedExtraction: extraction }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['document', vars.id] });
    },
  });
}

export function useReprocessDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/documents/${id}/reprocess`, {}),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['document', id] });
    },
  });
}

// ─── Deadlines ────────────────────────────────

export function useDeadlines(params: DeadlineListParams = {}) {
  return useQuery({
    queryKey: ['deadlines', params],
    queryFn: () => api.get<PaginatedResponse<Deadline & { clientBusinessName?: string; clientAfm?: string; obligationNameEl?: string; obligationNameEn?: string; obligationCode?: string; obligationFrequency?: string }>>(`/deadlines?${toQuery(params as Record<string, unknown>)}`),
  });
}

export function useDeadline(id: string | undefined) {
  return useQuery({
    queryKey: ['deadline', id],
    queryFn: () => api.get<{ data: Deadline & { client: { id: string; businessName: string; afm: string }; obligation: { id: string; code: string; nameEl: string; nameEn: string } } }>(`/deadlines/${id}`),
    enabled: !!id,
  });
}

export function useUpdateDeadline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDeadlineRequest }) =>
      api.patch(`/deadlines/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deadlines'] });
    },
  });
}

export function useGenerateDeadlines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { clientId: string; year: number }) =>
      api.post<{ createdCount: number }>('/deadlines/generate', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deadlines'] }),
  });
}

export function useTaxObligations() {
  return useQuery({
    queryKey: ['taxObligations'],
    queryFn: () => api.get<{ data: TaxObligation[] }>('/deadlines/obligations'),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Users ────────────────────────────────────

interface UserWithRole {
  id: string;
  email: string;
  name: string;
  roleTemplateId: string | null;
  roleTemplateName: string | null;
  isActive: boolean;
  isPending: boolean;
  createdAt: string;
  updatedAt: string;
  assignedClientCount: number;
}

interface MeResponse {
  id: string;
  email: string;
  name: string;
  officeId: string;
  roleTemplateId: string | null;
  isActive: boolean;
  roleTemplateName: string | null;
  capabilities: string[];
  officeName: string;
  officeTier: string;
  assignedClientIds: string[] | null;
}

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ data: UserWithRole[] }>('/users'),
  });
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/users/me'),
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<{ data: import('@taxistry/types').RoleTemplate[] }>('/users/roles'),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Notification Preferences ─────────────────

interface NotificationChannelPrefs {
  email?: boolean;
  sms?: boolean;
  inApp?: boolean;
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: () => api.get<{ preferences: Record<string, NotificationChannelPrefs> }>('/users/me/notifications'),
    staleTime: 60 * 1000,
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (preferences: Record<string, NotificationChannelPrefs>) =>
      api.patch('/users/me/notifications', { preferences }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notificationPreferences'] }),
  });
}

// ─── In-App Notifications ─────────────────────

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown> | null;
  status: string;
  readAt: string | null;
  createdAt: string;
}

export function useNotifications(limit = 20) {
  return useQuery({
    queryKey: ['notifications', limit],
    queryFn: () => api.get<{ data: NotificationItem[]; unreadCount: number }>(`/notifications?limit=${limit}`),
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch(`/notifications/${id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/notifications/read-all', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// ─── Activity Log ────────────────────────────

interface ActivityEntry {
  id: string;
  clientId: string | null;
  clientBusinessName: string | null;
  userId: string | null;
  userName: string | null;
  contactId: string | null;
  contactName: string | null;
  action: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export function useActivity(params: { clientId?: string; userId?: string; page?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: ['activity', params],
    queryFn: () => api.get<PaginatedResponse<ActivityEntry>>(`/activity?${toQuery(params as Record<string, unknown>)}`),
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { clientId: string; note: string }) =>
      api.post('/activity/note', data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['activity', { clientId: vars.clientId }] }),
  });
}

// ─── Client Requests ─────────────────────────

interface ClientRequestItem {
  id: string;
  clientId: string;
  clientBusinessName: string;
  title: string;
  description: string;
  dueDate: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export function useClientRequests(params: { clientId?: string; status?: string; page?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: ['clientRequests', params],
    queryFn: () => api.get<PaginatedResponse<ClientRequestItem>>(`/requests?${toQuery(params as Record<string, unknown>)}`),
  });
}

export function useCreateClientRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { clientId: string; title: string; description: string; dueDate?: string }) =>
      api.post('/requests', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clientRequests'] }),
  });
}

export function useUpdateClientRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { status?: string; title?: string; description?: string; dueDate?: string | null } }) =>
      api.patch(`/requests/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clientRequests'] }),
  });
}

// ─── Onboarding ──────────────────────────────

interface OnboardingTemplate {
  id: string;
  officeId: string | null;
  name: string;
  legalForm: string | null;
  items: Array<{ key: string; label: string; description?: string; required?: boolean }>;
  isSystemDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface OnboardingChecklist {
  id: string;
  clientId: string;
  templateId: string;
  templateName: string;
  items: Array<{ key: string; label: string; description?: string; required?: boolean }>;
  itemsStatus: Record<string, boolean>;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useOnboardingTemplates() {
  return useQuery({
    queryKey: ['onboardingTemplates'],
    queryFn: () => api.get<{ data: OnboardingTemplate[] }>('/onboarding/templates'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useClientOnboarding(clientId: string | undefined) {
  return useQuery({
    queryKey: ['onboarding', clientId],
    queryFn: () => api.get<{ data: OnboardingChecklist[] }>(`/onboarding/clients/${clientId}`),
    enabled: !!clientId,
  });
}

export function useCreateOnboardingChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, templateId }: { clientId: string; templateId: string }) =>
      api.post(`/onboarding/clients/${clientId}`, { clientId, templateId }),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['onboarding', vars.clientId] }),
  });
}

export function useUpdateOnboardingChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itemsStatus }: { id: string; itemsStatus: Record<string, boolean> }) =>
      api.patch(`/onboarding/checklists/${id}`, { itemsStatus }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding'] }),
  });
}

// ─── myDATA ──────────────────────────────────

export function useMyDataCredentials() {
  return useQuery({
    queryKey: ['mydata', 'credentials'],
    queryFn: () => api.get<{ configured: boolean }>('/mydata/credentials'),
  });
}

export function useSetMyDataCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { userId: string; subscriptionKey: string }) =>
      api.put('/mydata/credentials', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mydata'] }),
  });
}

export function useDeleteMyDataCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/mydata/credentials'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mydata'] }),
  });
}

export function useTestMyDataConnection() {
  return useMutation({
    mutationFn: (creds: { userId: string; subscriptionKey: string }) =>
      api.post<{ success: boolean; error?: string }>('/mydata/credentials/test', creds),
  });
}

export function useMyDataSyncs(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['mydata', 'syncs', params],
    queryFn: () => api.get<PaginatedResponse<{
      id: string;
      clientId: string;
      clientBusinessName: string;
      clientAfm: string;
      overallStatus: string;
      transmittedCount: number;
      receivedCount: number;
      pendingClassificationCount: number;
      rejectedCount: number;
      gapCount: number;
      lastPolledAt: string | null;
      lastSuccessfulAt: string | null;
      lastError: string | null;
      pollPriority: string;
    }>>(`/mydata/syncs?${toQuery(params)}`),
  });
}

export function useMyDataSummary() {
  return useQuery({
    queryKey: ['mydata', 'summary'],
    queryFn: () => api.get<{
      totalClients: number;
      greenCount: number;
      yellowCount: number;
      redCount: number;
      unknownCount: number;
      totalRejected: number;
      totalGaps: number;
      totalPendingClassification: number;
    }>('/mydata/summary'),
  });
}

export function useInitMyDataSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => api.post('/mydata/syncs/init', { clientId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mydata', 'syncs'] }),
  });
}

export function useTriggerMyDataSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => api.post(`/mydata/syncs/${clientId}/trigger`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mydata', 'syncs'] }),
  });
}

export function useMyDataInvoices(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['mydata', 'invoices', params],
    queryFn: () => api.get<PaginatedResponse<{
      id: string;
      clientId: string;
      mark: number;
      direction: string;
      invoiceType: string | null;
      series: string | null;
      aa: string | null;
      issueDate: string | null;
      issuerAfm: string | null;
      issuerName: string | null;
      counterpartAfm: string | null;
      counterpartName: string | null;
      netValue: string | null;
      vatAmount: string | null;
      totalValue: string | null;
      incomeClassified: boolean;
      expenseClassified: boolean;
      cancelled: boolean;
      hasRejection: boolean;
      hasGap: boolean;
    }>>(`/mydata/invoices?${toQuery(params)}`),
    enabled: params.clientId !== undefined,
  });
}

export function useMyDataInvoice(invoiceId: string | null) {
  return useQuery({
    queryKey: ['mydata', 'invoice', invoiceId],
    queryFn: () => api.get<{
      id: string;
      clientId: string;
      mark: string;
      uid: string | null;
      direction: string;
      invoiceType: string | null;
      series: string | null;
      aa: string | null;
      issueDate: string | null;
      issuerAfm: string | null;
      issuerName: string | null;
      counterpartAfm: string | null;
      counterpartName: string | null;
      netValue: string | null;
      vatAmount: string | null;
      totalValue: string | null;
      incomeClassified: boolean;
      expenseClassified: boolean;
      cancelled: boolean;
      cancellationMark: string | null;
      hasRejection: boolean;
      rejectionErrors: Array<{ code: string; message?: string }> | null;
      hasGap: boolean;
      createdAt: string;
      updatedAt: string;
      rejectionExplanations: Array<{
        code: string;
        originalMessage: string;
        explanation: string;
        suggestion: string;
      }> | null;
    }>(`/mydata/invoices/${invoiceId}`),
    enabled: !!invoiceId,
  });
}

// ─── Tasks ───────────────────────────────────

interface TaskItem {
  id: string;
  officeId: string;
  clientId: string | null;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  source: string;
  parentTaskId: string | null;
  sortOrder: number;
  assignedToUserId: string | null;
  dueDate: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  clientBusinessName?: string;
  assignedToName?: string;
  subtaskCount?: number;
}

export function useTasks(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: () => api.get<PaginatedResponse<TaskItem>>(`/tasks?${toQuery(params)}`),
  });
}

export function useTaskBoard() {
  return useQuery({
    queryKey: ['tasks', 'board'],
    queryFn: () => api.get<{ todo: TaskItem[]; inProgress: TaskItem[]; done: TaskItem[] }>('/tasks/board'),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      title: string;
      description?: string;
      clientId?: string;
      priority?: string;
      assignedToUserId?: string;
      dueDate?: string;
      parentTaskId?: string;
    }) => api.post<TaskItem>('/tasks', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<TaskItem>(`/tasks/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

// ─── Messages ───────────────────────────────

interface ThreadItem {
  id: string;
  officeId: string;
  clientId: string;
  clientBusinessName: string | null;
  subject: string | null;
  taskId: string | null;
  requestId: string | null;
  deadlineId: string | null;
  documentId: string | null;
  isArchived: boolean;
  lastMessageAt: string | null;
  lastOfficeReadAt: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessagePreview: string | null;
  lastMessageDirection: string | null;
  lastMessageIsAutomated: boolean;
}

interface MessageItem {
  id: string;
  threadId: string;
  direction: string;
  senderUserId: string | null;
  senderContactId: string | null;
  senderUserName: string | null;
  senderContactName: string | null;
  body: string;
  attachments: Array<{ storageKey: string; filename: string; mimeType: string; sizeBytes: number }>;
  isAutomated: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export function useMessageThreads(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['messageThreads', params],
    queryFn: () => api.get<PaginatedResponse<ThreadItem>>(`/messages/threads?${toQuery(params)}`),
  });
}

export function useMessageThread(threadId: string | null) {
  return useQuery({
    queryKey: ['messageThread', threadId],
    queryFn: () => api.get<ThreadItem & { messages: PaginatedResponse<MessageItem> }>(`/messages/threads/${threadId}`),
    enabled: !!threadId,
  });
}

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { clientId: string; subject?: string; body: string; taskId?: string; requestId?: string; deadlineId?: string; documentId?: string }) =>
      api.post('/messages/threads', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messageThreads'] }),
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, body }: { threadId: string; body: string }) =>
      api.post(`/messages/threads/${threadId}/messages`, { body }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['messageThreads'] });
      qc.invalidateQueries({ queryKey: ['messageThread', vars.threadId] });
    },
  });
}

export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) =>
      api.post(`/messages/threads/${threadId}/read`, {}),
    onSuccess: (_d, threadId) => {
      qc.invalidateQueries({ queryKey: ['messageThreads'] });
      qc.invalidateQueries({ queryKey: ['messageThread', threadId] });
      qc.invalidateQueries({ queryKey: ['messageUnreadCount'] });
    },
  });
}

export function useArchiveThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, isArchived }: { threadId: string; isArchived: boolean }) =>
      api.patch(`/messages/threads/${threadId}`, { isArchived }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messageThreads'] });
      qc.invalidateQueries({ queryKey: ['messageUnreadCount'] });
    },
  });
}

export function useMessageUnreadCount() {
  return useQuery({
    queryKey: ['messageUnreadCount'],
    queryFn: () => api.get<{ unreadCount: number }>('/messages/unread-count'),
    refetchInterval: 30_000,
  });
}
